import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, resolveDirective as _resolveDirective, withDirectives as _withDirectives, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "ml-auto"
}
const _hoisted_2 = ["textContent"]
const _hoisted_3 = ["textContent"]
const _hoisted_4 = ["textContent"]
const _hoisted_5 = { key: 1 }

import { computed, onMounted, Ref, ref } from 'vue';
import Column from 'primevue/column';
import DataTable from 'primevue/datatable';
import Chip from 'primevue/chip';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import ArchiveSwitch from '@/components/ArchiveSwitch';
import { useArchiveStore, useAuthStore, useClinicianStore } from '@/store';
import { ClinicianTableInterface } from '@/types';
import { can, isValidForRoleByTenant } from '@/composables';
import { PERMISSIONS } from '@/enum/permissions';


export default /*@__PURE__*/_defineComponent({
  __name: 'ClinicianTable',
  setup(__props) {

const archiveStore = useArchiveStore();
const authStore = useAuthStore();
const clinicianStore = useClinicianStore();
const clinicians: Ref<ClinicianTableInterface[]> = ref([]);
const clinicianCount: Ref<number> = computed(() => (clinicians.value ? clinicians.value.length : 0));
const clinicianModalOpen = ref(false);
const archivedVisible = ref(false);
const clinicianItems: Ref<{ name: string; value: number }[]> = ref([]);

onMounted((): void => {
  if (can([PERMISSIONS.ORGANIZATION_CLINICIANS_INDEX, PERMISSIONS.ORGANIZATION_CLINICIANS_INDEX_SELF]))
    getClinicians(false);
});

const getClinicians: (showArchived: boolean) => Promise<void> = async (showArchived: boolean) => {
  const filterArchived = showArchived ? '?archived=1' : '';
  const clinicianData = await clinicianStore.getCliniciansForTable(filterArchived).catch((e) => {
    clinicians.value = [];
    throw e;
  });
  const filteredClinicians = clinicianData.filter(
    (clinician) => clinician.organizations && clinician.organizations[0].id === clinicianStore.orgId,
  );
  const parentOrgClinicians = clinicianData.filter(
    (clinician) => clinician.organizations && clinician.organizations[0].id != clinicianStore.orgId,
  );
  clinicians.value = [
    ...filteredClinicians.sort((a, b) => a.name.localeCompare(b.name)),
    ...parentOrgClinicians.sort((a, b) => a.name.localeCompare(b.name)),
  ];
};

const viewActions = ref(can([PERMISSIONS.ORGANIZATION_DETACH_CLINICIANS, PERMISSIONS.PEOPLE_UNARCHIVE]));

const createMenuModel = (item: ClinicianTableInterface) => {
  const menuItems = [];
  if (!authStore.currentInstanceIsMasterTenant && can([PERMISSIONS.ORGANIZATION_DETACH_CLINICIANS])) {
    menuItems.push({
      label: 'Remove Service Provider',
      command: () => {
        detachClinicians([item.id], item.name);
      },
    });
  }
  if (item.archived && can([PERMISSIONS.PEOPLE_UNARCHIVE])) {
    menuItems.push({
      label: 'Unarchive',
      command: async () => {
        await archiveStore.unarchive(item.id);
        archiveStore.doToast(`${item.name} has been restored`, 'Unarchived');
      },
    });
  }
  return menuItems;
};

const attachClinicians: (ids: number[]) => Promise<void> = async (ids) => {
  const response = await clinicianStore.attachClinicians({ clinicians: ids });
  if (response.status === 200) {
    await getClinicians(false);
    clinicianStore.doToast('Service Provider(s) has been added', 'Added');
  } else {
    clinicianStore.doToast('Service Provider(s) has not been added', 'Not Added', true);
  }
};

const detachClinicians: (ids: number[], clinicianName: string) => Promise<void> = async (ids, clinicianName) => {
  const response = await clinicianStore.detachClinicians({ clinicians: ids });
  if (response.status === 200) {
    clinicianStore.doToast(`${clinicianName} has been removed`, 'Removed');
    await getClinicians(false);
  } else {
    clinicianStore.doToast(`${clinicianName} hasn't been removed`, 'Not Removed', true);
  }
};

const openAddClinicians = async () => {
  clinicianModalOpen.value = true;
  clinicianItems.value = await clinicianStore.getAddCliniciansModalData();
};

const onCancelAdd = () => (clinicianModalOpen.value = false);

const onSaveAdd = (clinicians: { name: string; value: number }[]) => {
  let clinicianIds = clinicians.map((clinician) => {
    return clinician.value;
  });
  attachClinicians(clinicianIds);
  clinicianModalOpen.value = false;
};

const showArchived = () => {
  archivedVisible.value = true;
  clinicianStore.showArchived = true;
  getClinicians(true);
};
const hideArchived = () => {
  archivedVisible.value = false;
  clinicianStore.showArchived = false;
  getClinicians(false);
};

const setRowClass = (rowData: ClinicianTableInterface) => {
  let style =
    rowData.organizations && rowData.organizations[0].id != clinicianStore.orgId
      ? 'inherited-service-provider-row'
      : 'service-provider-row';

  if (rowData.archived) style += ' grey-row';

  return style;
};

return (_ctx: any,_cache: any) => {
  const _component_AppButton = _resolveComponent("AppButton")!
  const _component_Icon = _resolveComponent("Icon")!
  const _component_RouterLink = _resolveComponent("RouterLink")!
  const _component_ActionMenu = _resolveComponent("ActionMenu")!
  const _component_Card = _resolveComponent("Card")!
  const _directive_tooltip = _resolveDirective("tooltip")!

  return (_openBlock(), _createBlock(_component_Card, null, {
    header: _withCtx(() => [
      _cache[0] || (_cache[0] = _createElementVNode("h2", null, "Service Providers", -1)),
      _createVNode(_unref(Chip), null, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(clinicianCount.value), 1)
        ]),
        _: 1
      }),
      (_unref(can)([_unref(PERMISSIONS).ORGANIZATIONS_CLINICIAN_SHOW_ARCHIVE]))
        ? (_openBlock(), _createBlock(_unref(ArchiveSwitch), {
            key: 0,
            onEnable: showArchived,
            onDisable: hideArchived
          }))
        : _createCommentVNode("", true),
      (!_unref(authStore).currentInstanceIsMasterTenant && _unref(can)([_unref(PERMISSIONS).ORGANIZATION_ATTACH_CLINICIANS]))
        ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
            _createVNode(_component_AppButton, {
              id: "add-button",
              type: "text",
              size: "small",
              text: "Add Service Providers",
              "icon-left": "add",
              onClick: openAddClinicians
            })
          ]))
        : _createCommentVNode("", true)
    ]),
    content: _withCtx(() => [
      _createVNode(_unref(DataTable), {
        value: clinicians.value,
        "global-filter-fields": ['name'],
        scrollable: true,
        "loading-icon": "logo-spinner",
        loading: _unref(clinicianStore).loading,
        "data-key": "name",
        "responsive-layout": "scroll",
        "row-class": setRowClass,
        "scroll-height": "440px",
        "state-key": "session-store-clinicians-table",
        "state-storage": "session",
        class: _normalizeClass({ 'table-loading': _unref(clinicianStore).loading })
      }, {
        empty: _withCtx(() => [...(_cache[1] || (_cache[1] = [
          _createTextVNode("Service Provider not assigned", -1)
        ]))]),
        default: _withCtx(() => [
          (clinicianCount.value)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createVNode(_unref(Column), {
                  "body-style": "max-width: 20rem",
                  field: "name",
                  header: "Service Provider Name",
                  "header-style": "max-width: 20rem;",
                  sortable: ""
                }, {
                  body: _withCtx(({ data }) => [
                    (data.archived)
                      ? _withDirectives((_openBlock(), _createBlock(_component_Icon, {
                          key: 0,
                          name: "info",
                          class: "mr-1 text-color-gray-dark text-base cursor-pointer"
                        }, null, 512)), [
                          [
                            _directive_tooltip,
                            {
                  value: 'Archived',
                  escape: true,
                  fitContent: true,
                },
                            void 0,
                            { top: true }
                          ]
                        ])
                      : _createCommentVNode("", true),
                    (_unref(can)([_unref(PERMISSIONS).PEOPLE_SHOW]) && _unref(isValidForRoleByTenant)(data.people_type))
                      ? (_openBlock(), _createBlock(_component_RouterLink, {
                          key: 1,
                          to: `/user/${data.id}`,
                          class: "font-semibold",
                          textContent: _toDisplayString(data.name)
                        }, null, 8, ["to", "textContent"]))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 2,
                          class: "font-semibold",
                          textContent: _toDisplayString(data.name)
                        }, null, 8, _hoisted_2))
                  ]),
                  _: 1
                }),
                _createVNode(_unref(Column), {
                  field: "disciplines",
                  header: "Disciplines"
                }, {
                  body: _withCtx(({ data }) => [
                    _createElementVNode("span", {
                      textContent: _toDisplayString(data.disciplines.join(', '))
                    }, null, 8, _hoisted_3)
                  ]),
                  _: 1
                }),
                _createVNode(_unref(Column), {
                  field: "organizations",
                  header: "Organization"
                }, {
                  body: _withCtx(({ data }) => [
                    (data.organizations[0].id != _unref(clinicianStore).orgId)
                      ? (_openBlock(), _createBlock(_component_RouterLink, {
                          key: 0,
                          class: "text-color-secondary underline",
                          to: `/organizations/${data.organizations[0].id}`,
                          textContent: _toDisplayString(data.organizations[0].name)
                        }, null, 8, ["to", "textContent"]))
                      : (_openBlock(), _createElementBlock("span", {
                          key: 1,
                          textContent: _toDisplayString(data.organizations[0].name)
                        }, null, 8, _hoisted_4))
                  ]),
                  _: 1
                }),
                (viewActions.value)
                  ? (_openBlock(), _createBlock(_unref(Column), {
                      key: 0,
                      "body-style": "justify-content: center; max-width: 10rem;",
                      "header-style": "justify-content: center; max-width: 10rem",
                      field: "id",
                      header: "Actions"
                    }, {
                      body: _withCtx(({ data }) => [
                        (data.organizations[0].id == _unref(clinicianStore).orgId && createMenuModel(data).length)
                          ? (_openBlock(), _createBlock(_component_ActionMenu, {
                              key: 0,
                              "menu-items": createMenuModel(data),
                              icon: "more_horiz"
                            }, null, 8, ["menu-items"]))
                          : (_openBlock(), _createElementBlock("span", _hoisted_5, "-"))
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true)
              ], 64))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["value", "loading", "class"]),
      (clinicianModalOpen.value && _unref(can)([_unref(PERMISSIONS).ORGANIZATION_ATTACH_CLINICIANS]))
        ? (_openBlock(), _createBlock(AddPersonModal, {
            key: 0,
            title: "Add service providers",
            items: clinicianItems.value,
            searchable: true,
            "button-text": "Add service providers",
            placeholder: "Search service providers",
            "person-type": "clinician",
            onAddItems: onSaveAdd,
            onCancel: onCancelAdd
          }, null, 8, ["items"]))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})